// Hook driving the Patient Merge page state and submission.
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import messages from "./messages";
import {
  InitialPatient,
  PatientFieldSelections,
  PatientInMerge,
  PatientMergeError,
  PatientMergeFieldKey,
  PatientMergeResult,
} from "./types";
import getPatientForMerge from "./getPatientForMerge";
import {
  hasCheckoutAppointment,
  hasConflictingAppointments,
} from "./checkPatientMergeBlockers";
import { PatientMergeSearchResult } from "./useSearchPatientsForMerge";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";

const FIELD_KEYS: PatientMergeFieldKey[] = [
  "customer",
  "mrn",
  "firstName",
  "lastName",
  "dateBirth",
  "address",
  "race",
  "ethnicity",
  "sex",
];

const emptySelections = (): PatientFieldSelections =>
  Object.fromEntries(
    FIELD_KEYS.map((k) => [k, null]),
  ) as PatientFieldSelections;

const getFieldValue = (
  patient: PatientInMerge,
  field: PatientMergeFieldKey,
): string | null => {
  switch (field) {
    case "customer":
      return patient.linkedCustomer?.id ?? null;
    case "mrn":
      return patient.mrn || null;
    case "firstName":
      return patient.firstName || null;
    case "lastName":
      return patient.lastName || null;
    case "dateBirth":
      return patient.dateBirth;
    case "address":
      return [
        patient.city,
        patient.state,
        patient.postalCode,
        patient.country,
      ].some(Boolean)
        ? "has-value"
        : null;
    case "race":
      return patient.race;
    case "ethnicity":
      return patient.ethnicity;
    case "sex":
      return patient.sex;
    default:
      return null;
  }
};

const usePatientMerge = (initialPatient: InitialPatient, isOpen: boolean) => {
  const mergePatient = useFirebaseAppFunction("mergePatient");
  const { formatMessage } = useIntl();

  const [patients, setPatients] = useState<PatientInMerge[]>([]);
  const [fieldSelections, setFieldSelections] =
    useState<PatientFieldSelections>(emptySelections());
  const [isWard, setIsWard] = useState(false);
  const [isFamilyMember, setIsFamilyMember] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mergeResult, setMergeResult] = useState<PatientMergeResult | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<PatientMergeError | null>(
    null,
  );

  const initialPatientRef = useRef(initialPatient);
  initialPatientRef.current = initialPatient;

  useEffect(() => {
    if (!isOpen) return;
    setPatients([initialPatientRef.current]);
    setFieldSelections(emptySelections());
    setIsWard(false);
    setIsFamilyMember(false);
    setMergeResult(null);
    setErrorMessage(null);
  }, [isOpen]);

  // AC26 — default the checkboxes to the winning Customer's existing
  // relationship, only when the winner itself changes (not on every
  // re-render, so a manual toggle survives adding another patient).
  const previousCustomerWinnerRef = useRef<string | null>(null);
  useEffect(() => {
    const winnerId = fieldSelections.customer;
    if (winnerId === previousCustomerWinnerRef.current) return;
    previousCustomerWinnerRef.current = winnerId;

    if (!winnerId) {
      setIsWard(false);
      setIsFamilyMember(false);
      return;
    }
    const winner = patients.find((p) => p.id === winnerId);
    const relationship = winner?.linkedCustomer?.relationship ?? null;
    setIsWard(relationship === "ward");
    setIsFamilyMember(relationship === "family");
  }, [fieldSelections.customer, patients]);

  const addPatient = useCallback(
    async (result: PatientMergeSearchResult) => {
      if (patients.some((p) => p.id === result.id)) return;
      const full = await getPatientForMerge(result.id);
      if (!full) return;
      setPatients((prev) => {
        if (prev.some((p) => p.id === full.id)) return prev;
        return [...prev, full];
      });
    },
    [patients],
  );

  const removePatient = useCallback((patientId: string) => {
    setPatients((prev) => prev.filter((p) => p.id !== patientId));
    setFieldSelections((prev) =>
      (Object.keys(prev) as PatientMergeFieldKey[]).reduce(
        (acc, key) => ({
          ...acc,
          [key]: prev[key] === patientId ? null : prev[key],
        }),
        {} as PatientFieldSelections,
      ),
    );
  }, []);

  const setFieldSelection = useCallback(
    (field: PatientMergeFieldKey, patientId: string) => {
      setFieldSelections((prev) => ({ ...prev, [field]: patientId }));
    },
    [],
  );

  // AC9/AC10 — mirrors mergePatientHandler's own guards client-side, which
  // still re-run server-side as the authoritative check.
  const blockerError: PatientMergeError | null = useMemo(() => {
    if (hasCheckoutAppointment(patients)) {
      return { title: formatMessage(messages.errorCheckoutAppointment) };
    }
    if (hasConflictingAppointments(patients)) {
      return { title: formatMessage(messages.errorConflictingAppointments) };
    }
    return null;
  }, [patients, formatMessage]);

  const canSubmit =
    patients.length >= 2 &&
    !blockerError &&
    FIELD_KEYS.every((field) => {
      const anyPatientHasValue = patients.some(
        (p) => getFieldValue(p, field) !== null,
      );
      if (!anyPatientHasValue) return true;

      const selectedId = fieldSelections[field];
      if (!selectedId) return false;
      const patient = patients.find((p) => p.id === selectedId);
      if (!patient) return false;
      return getFieldValue(patient, field) !== null;
    });

  // The Patient whose MRN radio is selected becomes the selected Patient.
  const selectedPatientId = fieldSelections.mrn;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !selectedPatientId) return;

    const selectedPatient = patients.find((p) => p.id === selectedPatientId);
    if (!selectedPatient) return;

    const nonSelectedPatientIds = patients
      .filter((p) => p.id !== selectedPatientId)
      .map((p) => p.id);

    const addressOwner = fieldSelections.address
      ? patients.find((p) => p.id === fieldSelections.address)
      : null;

    const customerOwner = fieldSelections.customer
      ? patients.find((p) => p.id === fieldSelections.customer)
      : null;

    // The merged profile takes the first/last name picked in the radio
    // columns, which may come from a non-selected Patient — the success
    // alert must show that resolved name, not the selected Patient's
    // pre-merge one.
    const resolvedFirstName =
      (fieldSelections.firstName &&
        patients.find((p) => p.id === fieldSelections.firstName)?.firstName) ||
      selectedPatient.firstName;
    const resolvedLastName =
      (fieldSelections.lastName &&
        patients.find((p) => p.id === fieldSelections.lastName)?.lastName) ||
      selectedPatient.lastName;
    const resolvedName = `${resolvedFirstName} ${resolvedLastName}`;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const { data: response } = await mergePatient({
        selectedPatientId,
        nonSelectedPatientIds,
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
        // date_birth is NOT NULL on patients — nullable in PatientInMerge only
        // as general defensive typing; guaranteed present in practice.
        dateBirth: ((fieldSelections.dateBirth &&
          patients.find((p) => p.id === fieldSelections.dateBirth)
            ?.dateBirth) ||
          selectedPatient.dateBirth) as string,
        city: addressOwner?.city ?? null,
        state: addressOwner?.state ?? null,
        postalCode: addressOwner?.postalCode ?? null,
        country: addressOwner?.country ?? null,
        race:
          (fieldSelections.race &&
            patients.find((p) => p.id === fieldSelections.race)?.race) ??
          null,
        ethnicity:
          (fieldSelections.ethnicity &&
            patients.find((p) => p.id === fieldSelections.ethnicity)
              ?.ethnicity) ??
          null,
        sex:
          (fieldSelections.sex &&
            patients.find((p) => p.id === fieldSelections.sex)?.sex) ??
          null,
        customerId: customerOwner?.linkedCustomer?.id ?? null,
        isWard,
        isFamilyMember,
      });

      if (response.status === "error") {
        const errorMap: Record<string, PatientMergeError> = {
          CHECKOUT_APPOINTMENT_EXISTS: {
            title: formatMessage(messages.errorCheckoutAppointment),
          },
          CONFLICTING_APPOINTMENTS: {
            title: formatMessage(messages.errorConflictingAppointments),
          },
          RECORD_LOCKED: { title: formatMessage(messages.errorRecordLocked) },
          PACS_MERGE_FAILED: {
            title: formatMessage(messages.errorPacsMergeFailed, {
              name: resolvedName,
            }),
          },
        };
        setErrorMessage(
          errorMap[response.error.code] ?? {
            title: formatMessage(messages.errorGeneric),
          },
        );
        return;
      }

      setMergeResult({
        selectedPatientId: response.data.selectedPatientId,
        selectedPatientMrn: selectedPatient.mrn,
        selectedPatientName: resolvedName,
        mergeStatus: response.data.mergeStatus,
      });
    } catch {
      setErrorMessage({ title: formatMessage(messages.errorGeneric) });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    patients,
    fieldSelections,
    isWard,
    isFamilyMember,
    mergePatient,
    selectedPatientId,
    formatMessage,
  ]);

  return {
    patients,
    fieldSelections,
    isWard,
    isFamilyMember,
    canSubmit,
    isSubmitting,
    mergeResult,
    errorMessage,
    blockerError,
    addPatient,
    removePatient,
    setFieldSelection,
    setIsWard,
    setIsFamilyMember,
    handleSubmit,
  };
};

export default usePatientMerge;
